import React, { useState, useEffect } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Input, IconButton } from "@material-tailwind/react";
import { Edit, Coins, Wallet, CalendarClock } from "lucide-react";
import { getSlabPayment } from "../../services/sales";
// v2 (F8): slab payment plan + isSlab predicate (§E; statusType→wbsStage embed)
import { getSlabPaymentPlan } from "../../services/wbs/index";
import { isSlab } from "../../constants/wbsStages";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { GradientButton, PrimaryButton } from "../Buttons";
import CustomInput from "../CustomInput";

const PaymentTable = ({
  formData,
  handleInputChange,
  setFormData,
  paymentType,
  onTotalAmountChange,
  property,
  onPaymentTableDataChange,
  showRemainingAmountToast,
  onNextClick,
}) => {

  const paymentTypeIds = {
    TOKEN: 1,
    SCHEDULED_PAYMENT: 2,
    FULL_PAYMENT: 3,
    DOWN_PAYMENT: 4,
    SLAB_WISE_PAYMENT: 5,
    HYBRID_PAYMENT: 6,
  };
  const [localPaymentPlan, setLocalPaymentPlan] = useState(
    formData.paymentPlan || []
  );

  const [remainingAmount, setRemainingAmount] = useState(
    parseFloat(formData.totalAmount) || 0
  );

  const [savedCardStates, setSavedCardStates] = useState(() => ({
    TOKEN: Boolean(formData.tokenTotalAmount && formData.tokenProjectedDate),
    DOWN_PAYMENT: Boolean(formData.downPaymentTotalAmount && formData.downPaymentProjectedDate),
    SCHEDULED: Boolean(formData.paymentPlan?.length > 0),
    SLAB_WISE: Boolean(formData.slabWisePayments?.length > 0),
    HYBRID: Boolean(formData.hybridPayments?.length > 0)
  }));

  // Replace the current savedData initialization with:
  const [savedData, setSavedData] = useState(() => {
    const initialData = [];

    // Add token payment if it exists
    if (formData.tokenTotalAmount && formData.tokenProjectedDate) {
      initialData.push({
        customerSalesId: 0,
        projectedAmount: parseFloat(formData.tokenTotalAmount),
        receivedAmount: null,
        projectedDate: formData.tokenProjectedDate,
        receivedDate: null,
        details: "",
        paymentTypeId: paymentTypeIds.TOKEN,
        id: crypto.randomUUID()
      });
    }

    // Add down payment if it exists
    if (formData.downPaymentTotalAmount && formData.downPaymentProjectedDate) {
      initialData.push({
        customerSalesId: 0,
        projectedAmount: parseFloat(formData.downPaymentTotalAmount),
        receivedAmount: null,
        projectedDate: formData.downPaymentProjectedDate,
        receivedDate: null,
        details: "",
        paymentTypeId: paymentTypeIds.DOWN_PAYMENT
      });
    }

    // Add scheduled payments if they exist
    if (formData.paymentPlan?.length > 0) {
      formData.paymentPlan.forEach(payment => {
        initialData.push({
          ...payment,
          customerSalesId: 0,
          receivedAmount: null,
          receivedDate: null,
          details: "",
          paymentTypeId: formData.paymentPlan.length === 1 ? paymentTypeIds.FULL_PAYMENT : paymentTypeIds.SCHEDULED_PAYMENT
        });
      });
    }


    // Add slab payments if they exist
    if (formData.slabWisePayments?.length > 0) {
      formData.slabWisePayments.forEach(slab => {
        initialData.push({
          ...slab,
          customerSalesId: 0,
          receivedAmount: null,
          receivedDate: null,
          details: "",
          paymentTypeId: paymentTypeIds.SLAB_WISE_PAYMENT
        });
      });
    }

    return initialData;
  });
  const [errorMessages] = useState({
    TOKEN: null,
    DOWN_PAYMENT: null,
    SCHEDULED: null,
    SLAB_WISE: null,
    HYBRID: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  
  // Helper function to get storage key
  const getStorageKey = () => `payment_data_${property?.propertyId || formData?.propertyId || 'default'}`;
  
  // Initialize hybrid payments from savedData or localStorage
  const initialScheduledPayments = savedData.filter(
    item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT
  );
  const initialSlabPayments = savedData.filter(
    item => item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT
  );

  // Try to load from localStorage on initial mount
  const getFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading from localStorage:', e);
    }
    return null;
  };

  const savedLocalData = getFromLocalStorage();
  const localScheduled = savedLocalData?.scheduledPayments || initialScheduledPayments;
  const localSlabs = savedLocalData?.slabPayments || initialSlabPayments;

  const [hybridScheduledPayments, setHybridScheduledPayments] = useState(localScheduled);
  const [hybridSlabPayments, setHybridSlabPayments] = useState(localSlabs);
  const [isHybridSlabGenerated, setIsHybridSlabGenerated] = useState(localSlabs.length > 0);
  const [isHybridScheduledSaved, setIsHybridScheduledSaved] = useState(localScheduled.length > 0);

  // Load from localStorage on mount - only if savedData is empty or small
  useEffect(() => {
    const storedData = getFromLocalStorage();
    console.log("Loading from localStorage, stored data count:", storedData?.savedData?.length || 0);
    if (storedData && storedData.savedData && Array.isArray(storedData.savedData)) {
      console.log("Found stored savedData with count:", storedData.savedData.length);
      // Only restore if localStorage has more items than current savedData (important for hybrid!)
      if (storedData.savedData.length > savedData.length) {
        console.log("Restoring savedData from localStorage:", storedData.savedData.length, "items");
        setSavedData(storedData.savedData);
        // Notify parent of restored data
        onPaymentTableDataChange(storedData.savedData);
      } else {
        console.log("Keeping existing savedData count:", savedData.length, "vs localStorage count:", storedData.savedData.length);
      }
      // Restore card states (always safe to do)
      if (storedData.savedCardStates) {
        setSavedCardStates(storedData.savedCardStates);
      }
      // Restore local payment plan (always safe to do)
      if (storedData.localPaymentPlan && Array.isArray(storedData.localPaymentPlan)) {
        setLocalPaymentPlan(storedData.localPaymentPlan);
      }
    }
    
    // Also check legacy hybrid storage
    const hybridStorageKey = `hybrid_payments_${property?.propertyId || formData?.propertyId || 'default'}`;
    const hybridData = localStorage.getItem(hybridStorageKey);
    if (hybridData) {
      try {
        const parsed = JSON.parse(hybridData);
        if (parsed.scheduledPayments && parsed.scheduledPayments.length > 0) {
          setHybridScheduledPayments(parsed.scheduledPayments);
          setIsHybridScheduledSaved(parsed.isScheduledSaved || false);
        }
        if (parsed.slabPayments && parsed.slabPayments.length > 0) {
          setHybridSlabPayments(parsed.slabPayments);
          setIsHybridSlabGenerated(parsed.isSlabGenerated || false);
        }
      } catch (e) {
        console.error('Error parsing hybrid data:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Save to localStorage when state changes
  useEffect(() => {
    console.log("Saving to localStorage with savedData count:", savedData.length);
    try {
      const dataToSave = {
        savedData: savedData,
        localPaymentPlan: localPaymentPlan,
        savedCardStates: savedCardStates,
        paymentType: paymentType,
        hybridScheduledPayments: hybridScheduledPayments,
        hybridSlabPayments: hybridSlabPayments,
        isHybridScheduledSaved: isHybridScheduledSaved,
        isHybridSlabGenerated: isHybridSlabGenerated
      };
      console.log("Saving complete payment data to localStorage:", JSON.stringify(dataToSave, null, 2));
      localStorage.setItem(getStorageKey(), JSON.stringify(dataToSave));
    } catch (e) {
      console.error('Error saving payment data to localStorage:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedData, localPaymentPlan, savedCardStates, hybridScheduledPayments, hybridSlabPayments, isHybridScheduledSaved, isHybridSlabGenerated]); // Depend on the data

  // Sync state when savedData changes (for persistence)
  useEffect(() => {
    const savedScheduled = savedData.filter(
      item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT
    );
    const savedSlabs = savedData.filter(
      item => item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT
    );
    
    if (savedScheduled.length > 0 && hybridScheduledPayments.length === 0) {
      setHybridScheduledPayments(savedScheduled);
      setIsHybridScheduledSaved(true);
    }
    
    if (savedSlabs.length > 0 && hybridSlabPayments.length === 0) {
      setHybridSlabPayments(savedSlabs);
      setIsHybridSlabGenerated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedData]);

  // Function to recalculate the remaining amount
  const calculateRemainingAmount = (
    currentFormData,
    currentLocalPaymentPlan,
    currentSavedData,
    currentPaymentType
  ) => {
    // Calculate total user contribution (total amount minus bank loan)
    const totalAmount = parseFloat(currentFormData.totalAmount) || 0;
    const bankLoanAmount = currentFormData.isBankLoanInvolved ? parseFloat(currentFormData.loanAmount) || 0 : 0;
    let calculatedRemaining = totalAmount - bankLoanAmount;

    // Subtract token payment if exists
    if (currentFormData.tokenTotalAmount) {
      const tokenAmount = parseFloat(currentFormData.tokenTotalAmount) || 0;
      calculatedRemaining -= tokenAmount;
    }

    // Subtract down payment if exists
    if (currentFormData.downPaymentTotalAmount) {
      const downPaymentAmount = parseFloat(currentFormData.downPaymentTotalAmount) || 0;
      calculatedRemaining -= downPaymentAmount;
    }

    // Subtract user's scheduled payments
    if (currentLocalPaymentPlan && currentLocalPaymentPlan.length > 0) {
      currentLocalPaymentPlan.forEach((installment) => {
        if (installment.projectedAmount && installment.paymentSource === "USER") {
          calculatedRemaining -= parseFloat(installment.projectedAmount) || 0;
        }
      });
    }

    // Subtract user's slab payments
    if (
      currentSavedData &&
      currentSavedData.length > 0 &&
      (currentPaymentType === "SLAB_WISE" || currentPaymentType === "HYBRID")
    ) {
      currentSavedData.forEach((slab) => {
        if (
          typeof slab === "object" &&
          slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT &&
          slab.paymentSource === "USER"
        ) {
          calculatedRemaining -= parseFloat(slab.projectedAmount || 0);
        }
      });
    }

    // For HYBRID payments, also subtract scheduled payments from savedData
    if (currentPaymentType === "HYBRID") {
      // Subtract scheduled payments from savedData
      currentSavedData.forEach((payment) => {
        if (
          typeof payment === "object" &&
          payment.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT &&
          payment.paymentSource === "USER"
        ) {
          calculatedRemaining -= parseFloat(payment.projectedAmount || 0);
        }
      });
    }

    // Ensure remaining amount doesn't go below 0
    return Math.max(0, calculatedRemaining).toFixed(2);
  };
  // Sync savedData with parent whenever it changes
  useEffect(() => {
    console.log("PaymentTable - savedData changed, syncing with parent. Count:", savedData.length);
    console.log("PaymentTable - savedData items:", savedData);
    if (savedData && savedData.length > 0) {
      console.log("Calling onPaymentTableDataChange with:", savedData.length, "items");
      onPaymentTableDataChange(savedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedData]); // Run whenever savedData changes
  useEffect(() => {
    const calculatedRemaining = calculateRemainingAmount(
      formData,
      localPaymentPlan,
      savedData,
      paymentType
    );
    setRemainingAmount(calculatedRemaining);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tokenTotalAmount, formData.downPaymentTotalAmount, formData, localPaymentPlan, savedData, paymentType]);

  useEffect(() => {
    if (remainingAmount !== 0) {
      showRemainingAmountToast(
        "Remaining amount is not zero. Please check the payment plan."
      );
    }
  }, [remainingAmount, showRemainingAmountToast]);
  useEffect(() => {
    setFormData((prev) => ({ ...prev, paymentPlan: localPaymentPlan }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPaymentPlan]);

  const handleEditCard = (cardType) => {
    setSavedCardStates((prev) => ({ ...prev, [cardType]: false }));
  };

  // ── Auto-capture (no Save click) ────────────────────────────────────────
  // Token, Down and Scheduled rows are now captured live as the user types,
  // mirroring the exact entry shape handleSaveCard used to build. Other
  // payment types already in savedData (slab/hybrid) are preserved untouched.
  // Depends only on the source inputs (not savedData) so there's no loop;
  // the JSON guard skips redundant parent notifications.
  useEffect(() => {
    // Token/Down are always auto-managed here. Scheduled/Full types are only
    // managed in SCHEDULED mode — in HYBRID/SLAB modes those rows are owned by
    // the hybrid/slab handlers, so we must NOT strip them.
    const MANAGED = [paymentTypeIds.TOKEN, paymentTypeIds.DOWN_PAYMENT];
    if (paymentType === "SCHEDULED") {
      MANAGED.push(paymentTypeIds.SCHEDULED_PAYMENT, paymentTypeIds.FULL_PAYMENT);
    }
    const gstEntry = (amount) => {
      const g = calculateGSTAmounts(amount);
      return {
        baseAmount: parseFloat(g.baseAmount.replace(/,/g, "")),
        cgstAmount: parseFloat(g.cgstAmount.replace(/,/g, "")),
        sgstAmount: parseFloat(g.sgstAmount.replace(/,/g, "")),
        igstAmount: parseFloat(g.igstAmount.replace(/,/g, "")),
      };
    };

    setSavedData((prev) => {
      // Reuse existing ids for stable rows so we don't churn on every keystroke.
      const prevByType = {};
      prev.forEach((it) => {
        if (prevByType[it.paymentTypeId] == null) prevByType[it.paymentTypeId] = it;
      });

      const others = prev.filter((it) => !MANAGED.includes(it.paymentTypeId));
      const managed = [];

      if (formData.tokenTotalAmount && formData.tokenProjectedDate) {
        managed.push({
          customerSalesId: 0,
          projectedAmount: parseFloat(formData.tokenTotalAmount) || 0,
          receivedAmount: null,
          projectedDate: formData.tokenProjectedDate || null,
          receivedDate: null,
          details: "",
          paymentTypeId: paymentTypeIds.TOKEN,
          ...gstEntry(formData.tokenTotalAmount),
          id: prevByType[paymentTypeIds.TOKEN]?.id || crypto.randomUUID(),
        });
      }

      if (formData.downPaymentTotalAmount && formData.downPaymentProjectedDate) {
        managed.push({
          customerSalesId: 0,
          projectedAmount: parseFloat(formData.downPaymentTotalAmount) || 0,
          receivedAmount: null,
          projectedDate: formData.downPaymentProjectedDate || null,
          receivedDate: null,
          details: "",
          paymentTypeId: paymentTypeIds.DOWN_PAYMENT,
          ...gstEntry(formData.downPaymentTotalAmount),
          id: prevByType[paymentTypeIds.DOWN_PAYMENT]?.id || crypto.randomUUID(),
        });
      }

      if (paymentType === "SCHEDULED" && localPaymentPlan.length > 0) {
        const typeId =
          localPaymentPlan.length === 1
            ? paymentTypeIds.FULL_PAYMENT
            : paymentTypeIds.SCHEDULED_PAYMENT;
        localPaymentPlan.forEach((inst) => {
          if (!inst.projectedAmount) return; // skip rows the user hasn't filled yet
          managed.push({
            ...inst,
            customerSalesId: 0,
            receivedAmount: null,
            receivedDate: null,
            details: "",
            paymentTypeId: typeId,
            ...gstEntry(inst.projectedAmount),
          });
        });
      }

      const next = [...others, ...managed];
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      onPaymentTableDataChange(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.tokenTotalAmount,
    formData.tokenProjectedDate,
    formData.downPaymentTotalAmount,
    formData.downPaymentProjectedDate,
    localPaymentPlan,
    paymentType,
  ]);

  // Hybrid payment functions
  const handleHybridScheduledChange = (index, field, value) => {
    setHybridScheduledPayments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Helper function to calculate remaining amount for slab payments
  const calculateRemainingForSlabs = () => {
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
    const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
    const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
    
    // Calculate total scheduled amount
    const totalScheduledAmount = hybridScheduledPayments.reduce((sum, payment) => 
      sum + (parseFloat(payment.projectedAmount || 0)), 0);
    
    // Calculate remaining amount for slab payments
    const remainingAmountForSlabs = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount - totalScheduledAmount;
    
    return {
      totalAmount,
      bankLoanAmount,
      tokenAmount,
      downPaymentAmount,
      totalScheduledAmount,
      remainingAmountForSlabs
    };
  };

  const handleSaveHybridScheduledPayments = () => {
    try {
      setIsLoading(true);
      
      // Validate scheduled payments
      if (hybridScheduledPayments.length === 0) {
        toast.error("Please add scheduled payments first.");
        return;
      }

      // Validate that all scheduled payments have amounts
      const hasInvalidScheduled = hybridScheduledPayments.some(payment => 
        !payment.projectedAmount || !payment.projectedDate
      );

      if (hasInvalidScheduled) {
        toast.error("Please fill all required fields for scheduled payments.");
        return;
      }

      // Add scheduled payments to savedData
      const combinedPayments = hybridScheduledPayments.map((payment, index) => {
        const gstAmounts = calculateGSTAmounts(payment.projectedAmount);
        
        // Calculate bank and user amounts based on payment source
        let bankAmount = 0;
        let userAmount = 0;
        
        if (payment.paymentSource === "BANK") {
          bankAmount = parseFloat(payment.projectedAmount) || 0;
        } else if (payment.paymentSource === "SPLIT") {
          bankAmount = parseFloat(payment.bankAmount || 0);
          userAmount = parseFloat(payment.userAmount || 0);
        } else {
          userAmount = parseFloat(payment.projectedAmount) || 0;
        }
        
        return {
          ...payment,
          customerSalesId: 0,
          receivedAmount: null,
          receivedDate: null,
          details: "",
          paymentTypeId: paymentTypeIds.SCHEDULED_PAYMENT,
          baseAmount: parseFloat(gstAmounts.baseAmount.replace(/,/g, '')),
          cgstAmount: parseFloat(gstAmounts.cgstAmount.replace(/,/g, '')),
          sgstAmount: parseFloat(gstAmounts.sgstAmount.replace(/,/g, '')),
          igstAmount: parseFloat(gstAmounts.igstAmount.replace(/,/g, '')),
          bankAmount: bankAmount,
          userAmount: userAmount,
          projectedAmount: parseFloat(payment.projectedAmount) || 0,
          id: payment.id || crypto.randomUUID()
        };
      });

      setSavedData((prev) => {
        // Filter out any existing scheduled payments
        const filteredData = prev.filter(item => 
          item.paymentTypeId !== paymentTypeIds.SCHEDULED_PAYMENT
        );

        // Add the scheduled payments
        const updatedData = [...filteredData, ...combinedPayments];

        console.log("handleSaveHybridScheduledPayments - Updated savedData. Total count:", updatedData.length);
        console.log("Updated data:", JSON.stringify(updatedData, null, 2));

        // Notify parent component of the change
        onPaymentTableDataChange(updatedData);

        return updatedData;
      });

      setIsHybridScheduledSaved(true);
      toast.success("Scheduled payments saved successfully! You can now generate slab payments.");
      setSavedCardStates((prev) => ({ ...prev, SCHEDULED: true }));
    } catch (error) {
      console.error("Error saving scheduled payments:", error);
      toast.error("Error saving scheduled payments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateHybridSlabPayments = async () => {
    try {
      setIsConfigLoading(true);
      
      // Calculate remaining amount after scheduled payments
      const calculation = calculateRemainingForSlabs();
      
      console.log("Hybrid Payment Calculation:");
      console.log("Total Amount:", calculation.totalAmount);
      console.log("Bank Loan Amount:", calculation.bankLoanAmount);
      console.log("Token Amount:", calculation.tokenAmount);
      console.log("Down Payment Amount:", calculation.downPaymentAmount);
      console.log("Total Scheduled Amount:", calculation.totalScheduledAmount);
      console.log("Remaining Amount for Slabs:", calculation.remainingAmountForSlabs);
      
      if (calculation.remainingAmountForSlabs <= 0) {
        toast.error("No remaining amount for slab payments. Please adjust scheduled payments.");
        return;
      }

      // v2 (F8/§E): getSlabPaymentPlan with camelCase params; response rows carry wbsStage embed
      const response = await getSlabPaymentPlan({
        propertyId: property.propertyId,
        totalAmount: calculation.remainingAmountForSlabs,
      });

      if (response && Array.isArray(response)) {
        // Filter out down payment and sort slabs by percentage
        const slabs = response.filter((slab) => slab.paymentName !== "DOWN_PAYMENT");

        if (slabs.length === 0) {
          toast.error("No slab data available for this property.");
          return;
        }

        // Calculate total percentage for distribution
        const totalPercentage = slabs.reduce((sum, slab) => sum + (slab.percentConsideration || 0), 0);

        // Create initial slabs with payment source determination
        let remainingUserAmount = calculation.remainingAmountForSlabs - calculation.bankLoanAmount;

        const newSlabPayments = slabs.map((slab) => {
          const slabPercentage = (slab.percentConsideration || 0) / totalPercentage;
          const slabAmount = calculation.remainingAmountForSlabs * slabPercentage;
          const id = crypto.randomUUID();
          let paymentSource = "USER";
          let bankAmount = 0;
          let userAmount = 0;

          // Calculate bank and user amounts - USER FIRST, then BANK
          if (remainingUserAmount > 0) {
            if (slabAmount <= remainingUserAmount) {
              // Can be paid entirely by user
              paymentSource = "USER";
              bankAmount = 0;
              userAmount = slabAmount;
              remainingUserAmount -= slabAmount;
            } else {
              // Split between user and bank
              paymentSource = "SPLIT";
              userAmount = remainingUserAmount;
              bankAmount = slabAmount - remainingUserAmount;
              remainingUserAmount = 0;
            }
          } else {
            // No user amount left, pay by bank
            paymentSource = "BANK";
            bankAmount = slabAmount;
            userAmount = 0;
          }

          const gstAmounts = calculateGSTAmounts(slabAmount);
          // v2 (F8/§E): wbsStage embed replaces statusType; retained: target/percentConsideration/paymentName/isCustom
          return {
            customerSalesId: 0,
            projectedAmount: slabAmount.toFixed(2),
            receivedAmount: null,
            projectedDate: null,
            receivedDate: null,
            details: "",
            paymentTypeId: paymentTypeIds.SLAB_WISE_PAYMENT,
            percentConsideration: slab.percentConsideration,
            paymentName: slab.wbsStage?.typeName || slab.statusType?.typeName || "N/A",
            wbsStageId: slab.wbsStage?.wbsStageId || slab.statusType?.statusTypeId,
            wbsStage: slab.wbsStage || slab.statusType,
            floorTarget: slab.floorTarget ?? slab.target,
            target: slab.target,
            isCustom: slab.isCustom ?? false,
            paymentSource: paymentSource,
            bankAmount: bankAmount.toFixed(2),
            userAmount: userAmount.toFixed(2),
            baseAmount: parseFloat(gstAmounts.baseAmount.replace(/,/g, '')),
            cgstAmount: parseFloat(gstAmounts.cgstAmount.replace(/,/g, '')),
            sgstAmount: parseFloat(gstAmounts.sgstAmount.replace(/,/g, '')),
            igstAmount: parseFloat(gstAmounts.igstAmount.replace(/,/g, '')),
            id: id
          };
        });

        setHybridSlabPayments(newSlabPayments);
        setIsHybridSlabGenerated(true);
        
        // Auto-save slab payments
        setSavedData((prev) => {
          // Filter out any existing slab payments
          const filteredData = prev.filter(item => 
            item.paymentTypeId !== paymentTypeIds.SLAB_WISE_PAYMENT
          );
          
          // Add the new slab payments
          const updatedData = [...filteredData, ...newSlabPayments];
          
          console.log("handleGenerateHybridSlabPayments - Updated savedData. Total count:", updatedData.length);
          console.log("Updated data:", JSON.stringify(updatedData, null, 2));
          
          // Notify parent component of the change
          onPaymentTableDataChange(updatedData);
          
          return updatedData;
        });
        
        toast.success("Slab payments generated and saved successfully!");
      }
    } catch (error) {
      console.error("Error fetching slab payment:", error);
      toast.error(
        "Error fetching slab payment: " +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleAutoGenerateSlabData = async () => {
    try {
      setIsConfigLoading(true);
      // Calculate remaining agreement value after token and down payment
      const totalAmount = parseFloat(formData.totalAmount) || 0;
      const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
      const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;

      // Calculate remaining agreement value for slab payments
      const remainingAgreementValue = totalAmount - tokenAmount - downPaymentAmount;

      if (remainingAgreementValue <= 0) {
        toast.error("Please enter valid agreement value and token/down payment amounts.");
        return;
      }

      // v2 (F8/§E): getSlabPaymentPlan with camelCase params
      const response = await getSlabPaymentPlan({
        propertyId: property.propertyId,
        totalAmount: remainingAgreementValue,
      });

      if (response && Array.isArray(response)) {
        // Calculate total amounts for bank loan
        const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;

        // Filter out down payment and sort slabs by percentage
        const slabs = response.filter((slab) => slab.paymentName !== "DOWN_PAYMENT");

        if (slabs.length === 0) {
          toast.error("No slab data available for this property.");
          return;
        }

        // Calculate total percentage for distribution
        const totalPercentage = slabs.reduce((sum, slab) => sum + (slab.percentConsideration || 0), 0);

        // Create initial slabs with payment source determination
        let remainingUserAmount = remainingAgreementValue - bankLoanAmount;

        const newData = slabs.map((slab) => {
          const slabPercentage = (slab.percentConsideration || 0) / totalPercentage;
          const slabAmount = remainingAgreementValue * slabPercentage;
          const id = crypto.randomUUID();
          let paymentSource = "USER";
          let bankAmount = 0;
          let userAmount = 0;

          // REVERSED: Calculate bank and user amounts - USER FIRST, then BANK
          if (remainingUserAmount > 0) {
            if (slabAmount <= remainingUserAmount) {
              // Can be paid entirely by user
              paymentSource = "USER";
              bankAmount = 0;
              userAmount = slabAmount;
              remainingUserAmount -= slabAmount;
            } else {
              // Split between user and bank
              paymentSource = "SPLIT";
              userAmount = remainingUserAmount;
              bankAmount = slabAmount - remainingUserAmount;
              remainingUserAmount = 0;
            }
          } else {
            // No user amount left, pay by bank
            paymentSource = "BANK";
            bankAmount = slabAmount;
            userAmount = 0;
          }

          const gstAmounts = calculateGSTAmounts(slabAmount);
          // v2 (F8/§E): wbsStage embed replaces statusType; retained: target/percentConsideration/paymentName/isCustom
          return {
            customerSalesId: 0,
            projectedAmount: slabAmount.toFixed(2),
            receivedAmount: null,
            projectedDate: null,
            receivedDate: null,
            details: "",
            paymentTypeId: paymentTypeIds.SLAB_WISE_PAYMENT,
            percentConsideration: slab.percentConsideration,
            paymentName: slab.wbsStage?.typeName || slab.statusType?.typeName || "N/A",
            wbsStageId: slab.wbsStage?.wbsStageId || slab.statusType?.statusTypeId,
            wbsStage: slab.wbsStage || slab.statusType,
            floorTarget: slab.floorTarget ?? slab.target,
            target: slab.target,
            isCustom: slab.isCustom ?? false,
            paymentSource: paymentSource,
            bankAmount: bankAmount.toFixed(2),
            userAmount: userAmount.toFixed(2),
            baseAmount: parseFloat(gstAmounts.baseAmount.replace(/,/g, '')),
            cgstAmount: parseFloat(gstAmounts.cgstAmount.replace(/,/g, '')),
            sgstAmount: parseFloat(gstAmounts.sgstAmount.replace(/,/g, '')),
            igstAmount: parseFloat(gstAmounts.igstAmount.replace(/,/g, '')),
            id: id
          };
        });

        setSavedData((prev) => {
          // Filter out existing slab wise payments
          const filteredData = prev.filter(item => item.paymentTypeId !== paymentTypeIds.SLAB_WISE_PAYMENT);
          const updatedData = [...filteredData, ...newData];
          onPaymentTableDataChange(updatedData);
          return updatedData;
        });
        setSavedCardStates((prev) => ({ ...prev, SLAB_WISE: true }));
        toast.success("Slab payments generated successfully!");
      }
    } catch (error) {
      console.error("Error fetching slab payment:", error);
      toast.error(
        "Error fetching slab payment: " +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setIsConfigLoading(false);
    }
  };
  const handleSlabChange = (index, field, value) => {
    setSavedData((prev) => {
      if (!prev || !Array.isArray(prev)) return prev;

      // Calculate total amount and bank loan
      const totalAmount = parseFloat(formData.totalAmount) || 0;
      const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
      const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
      const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
      const remainingAgreementValue = totalAmount - tokenAmount - downPaymentAmount;

      // If changing percentage: recalc this row's amount and bank/user split
      if (field === "percentConsideration") {
        const newPercent = parseFloat(value) || 0;
        const newAmount = remainingAgreementValue * (newPercent / 100);
        const totalUserResponsibility = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount;
        const allocatedUserAmount = prev.slice(0, index).reduce((sum, s) =>
          sum + (parseFloat(s.userAmount || 0)), 0);
        const allocatedBankAmount = prev.slice(0, index).reduce((sum, s) =>
          sum + (parseFloat(s.bankAmount || 0)), 0);
        const remainingUserAmount = Math.max(0, totalUserResponsibility - allocatedUserAmount);
        const remainingBankAmount = Math.max(0, bankLoanAmount - allocatedBankAmount);

        // Fill buyer responsibility first, overflow to bank — but never beyond
        // the bank's remaining capacity (the excess simply isn't allocated to
        // bank, avoiding a negative bank remaining).
        const userAmount = Math.min(newAmount, remainingUserAmount);
        const bankAmount = Math.min(newAmount - userAmount, remainingBankAmount);
        let paymentSource = "USER";
        if (userAmount > 0 && bankAmount > 0) paymentSource = "SPLIT";
        else if (bankAmount > 0) paymentSource = "BANK";

        const gstAmounts = calculateGSTAmounts(newAmount);
        const updated = prev.map((slab, i) => {
          if (i !== index) return slab;
          return {
            ...slab,
            percentConsideration: newPercent,
            projectedAmount: newAmount.toFixed(2),
            paymentSource,
            bankAmount: bankAmount.toFixed(2),
            userAmount: userAmount.toFixed(2),
            baseAmount: parseFloat(gstAmounts.baseAmount.replace(/,/g, '')),
            cgstAmount: parseFloat(gstAmounts.cgstAmount.replace(/,/g, '')),
            sgstAmount: parseFloat(gstAmounts.sgstAmount.replace(/,/g, '')),
            igstAmount: parseFloat(gstAmounts.igstAmount.replace(/,/g, '')),
          };
        });
        onPaymentTableDataChange(updated);
        return updated;
      }

      // If changing amount
      if (field === "projectedAmount") {
        const enteredAmount = parseFloat(value) || 0;

        const totalAmount = parseFloat(formData.totalAmount) || 0;
        const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
        const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
        const totalUserResponsibility = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount;

        // Amounts already committed in the rows BEFORE this one.
        const allocatedUserAmount = prev.slice(0, index).reduce((sum, s) =>
          sum + (parseFloat(s.userAmount || 0)), 0);
        const allocatedBankAmount = prev.slice(0, index).reduce((sum, s) =>
          sum + (parseFloat(s.bankAmount || 0)), 0);

        const remainingUserAmount = Math.max(0, totalUserResponsibility - allocatedUserAmount);
        const remainingBankAmount = Math.max(0, bankLoanAmount - allocatedBankAmount);
        // Cap so a slab can't exceed what's still unallocated across the
        // buyer's responsibility AND the bank loan (prevents negative bank
        // remaining from an over-large entry).
        const maxAllowed = remainingUserAmount + remainingBankAmount;

        let newAmount = enteredAmount;
        if (enteredAmount > maxAllowed) {
          newAmount = maxAllowed;
          toast.warn(`Amount capped to the remaining ${formatNumber(maxAllowed)}.`);
        }

        // Reset this row and clear subsequent rows.
        const resetPlan = prev.map((slab, i) => {
          if (i === index) {
            return { ...slab, projectedAmount: newAmount.toFixed(2), paymentSource: null, bankAmount: null, userAmount: null };
          } else if (i > index) {
            return { ...slab, projectedAmount: "", paymentSource: null, bankAmount: null, userAmount: null };
          }
          return slab;
        });

        // Fill the buyer's remaining responsibility first, overflow to bank
        // (bounded by the cap above).
        const userPortion = Math.min(newAmount, remainingUserAmount);
        const bankPortion = newAmount - userPortion;
        let source = "USER";
        if (userPortion > 0 && bankPortion > 0) source = "SPLIT";
        else if (bankPortion > 0) source = "BANK";

        resetPlan[index] = {
          ...resetPlan[index],
          projectedAmount: newAmount.toFixed(2),
          paymentSource: source,
          userAmount: userPortion.toFixed(2),
          bankAmount: bankPortion.toFixed(2),
        };

        return resetPlan;
      }

      // For other fields, just update the value
      const updatedSlabWise = prev.map((slab, i) => {
        if (i === index) {
          return {
            ...slab,
            [field]: value
          };
        }
        return slab;
      });

      return updatedSlabWise;
    });
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString();
    } catch (e) {
      return "N/A";
    }
  };
  const formatNumber = (number) => {
    return parseFloat(number || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper function to calculate GST amounts with CGST, SGST, IGST breakdown
  const calculateGSTAmounts = (totalAmount) => {
    const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
    const amount = parseFloat(totalAmount) || 0;
    const amountExclGst = amount / (1 + (gstPercent / 100));
    const gstAmount = amount - amountExclGst;

    // Check if customer and user states are the same
    const isSameState = formData.customerState === formData.userState;

    // Debug logging
    console.log("Customer State:", formData.customerState);
    console.log("User State:", formData.userState);
    console.log("States are same:", isSameState);

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    if (isSameState) {
      // Same state: IGST is the full GST amount
      cgstAmount = gstAmount / 2;
      sgstAmount = gstAmount / 2;
      igstAmount = 0;

    } else {
      // Different state: CGST and SGST are half of GST amount each
      igstAmount = gstAmount;
      cgstAmount = 0;
      sgstAmount = 0;
      console.log("Using CGST/SGST split (different states):", { cgstAmount, sgstAmount, igstAmount });
    }

    return {
      amountExclGst: formatNumber(amountExclGst),
      gstAmount: formatNumber(gstAmount),
      baseAmount: formatNumber(amountExclGst),
      cgstAmount: formatNumber(cgstAmount),
      sgstAmount: formatNumber(sgstAmount),
      igstAmount: formatNumber(igstAmount)
    };
  };

  // Add this new function to handle token amount changes
  const handleTokenAmountChange = (e) => {
    const { name, value } = e.target;
    const newTokenAmount = parseFloat(value) || 0;

    // Update form data with new token amount
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculate remaining amount after token deduction
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
    const remainingAmount = totalAmount - bankLoanAmount - newTokenAmount;

    // Update remaining amount
    setRemainingAmount(Math.max(0, remainingAmount).toFixed(2));
  };

  return (
    <>

      <div className=" max-h-[75vh] mt-4 flex flex-col ">


        <div className=" space-y-6 flex-1">
          {/* Token and Down Payment Cards Row */}
          <div className="grid grid-cols-2 gap-6">
            {/* Token Payment Card */}
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
              <h3 className="flex items-center gap-2 font-semibold text-slate-900">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600 ring-1 ring-flamingo-100">
                  <Coins size={16} />
                </span>
                Token Payment
              </h3>
              <div className="mt-2 space-y-4">
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block text-sm font-normal text-slate-700 mb-1">
                      Total Amount:
                    </label>
                    <Input.Field
                      type="number"
                      name="tokenTotalAmount"
                      value={formData.tokenTotalAmount || ""}
                      onChange={handleTokenAmountChange}
                      className="h-10 rounded-lg"
                      placeholder="Enter total amount"
                    />
                    {errorMessages.TOKEN && (
                      <p className="text-red-500 text-sm mt-1">
                        {errorMessages.TOKEN}
                      </p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-normal text-slate-700 mb-1">
                      Projected Date:
                    </label>
                    <CustomInput
                      type="date"
                      name="tokenProjectedDate"
                      value={formData.tokenProjectedDate || ""}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Down Payment Card */}
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
              <h3 className="flex items-center gap-2 font-semibold text-slate-900">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600 ring-1 ring-flamingo-100">
                  <Wallet size={16} />
                </span>
                Down Payment
              </h3>
              <div className="mt-2 space-y-4">
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block text-sm font-normal text-slate-700 mb-1">
                      Total Amount:
                    </label>
                    <Input.Field
                      type="number"
                      name="downPaymentTotalAmount"
                      value={formData.downPaymentTotalAmount || ""}
                      onChange={handleInputChange}
                      className="h-10 rounded-lg"
                      placeholder="Enter total amount"
                    />
                    {errorMessages.DOWN_PAYMENT && (
                      <p className="text-red-500 text-sm mt-1">
                        {errorMessages.DOWN_PAYMENT}
                      </p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-normal text-slate-700 mb-1">
                      Projected Date:
                    </label>
                    <CustomInput
                      type="date"
                      name="downPaymentProjectedDate"
                      value={formData.downPaymentProjectedDate || ""}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scheduled Payment Card */}
          {paymentType === "SCHEDULED" && (
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
              {!savedCardStates.SCHEDULED ? (
                <>
                  <h3 className="flex items-center gap-2 font-semibold text-slate-900">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600 ring-1 ring-flamingo-100">
                      <CalendarClock size={16} />
                    </span>
                    Scheduled Payment
                  </h3>
                  <div className="mt-2 space-y-4">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-normal text-slate-700 mb-1">
                          Number of Installments:
                        </label>
                        <Input.Field
                          type="number"
                          name="numberOfInstallments"
                          value={formData.numberOfInstallments || ""}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const installments = parseInt(inputValue, 10) || 0;
                            if (
                              installments >= 0 &&
                              Number.isInteger(installments) &&
                              installments < 1000
                            ) {
                              // Create basic installment structure
                              let newPaymentPlan = Array(installments)
                                .fill()
                                .map((_, index) => ({
                                  installmentNumber: index + 1,
                                  planType: installments === 1 ? "fullpayment" : "scheduled_payment",
                                  projectedAmount: "",
                                  projectedDate: null,
                                  statusTypeId: null,
                                  paymentSource: "USER",
                                  id: crypto.randomUUID()
                                }));

                              // If bank loan is involved, set last few installments as bank payments (REVERSED)
                              if (formData.isBankLoanInvolved) {
                                const bankLoanAmount = parseFloat(formData.loanAmount) || 0;
                                const totalAmount = parseFloat(formData.totalAmount) || 0;
                                const avgInstallmentAmount = totalAmount / installments;
                                const bankInstallments = Math.ceil(bankLoanAmount / avgInstallmentAmount);

                                newPaymentPlan = newPaymentPlan.map((installment, index) => {
                                  if (index >= installments - bankInstallments) {
                                    installment.paymentSource = "BANK";
                                  }
                                  return installment;
                                });
                              }

                              setLocalPaymentPlan(newPaymentPlan);
                            } else {
                              setLocalPaymentPlan([]);
                              if (inputValue) {
                                alert(
                                  "Please enter a valid positive integer for Number of Installments."
                                );
                              }
                            }
                            handleInputChange(e);
                          }}
                          className="h-10 w-1/4 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Payment Plan Table */}
                    <div className="overflow-x-auto">
                      {localPaymentPlan.length === 0 ? (
                        <div className="text-center py-8 bg-slate-50 rounded-lg">
                          <p className="text-slate-500">
                            Please enter the number of installments to view the payment schedule
                          </p>
                        </div>
                      ) : (
                        <table className="min-w-full divide-y divide-slate-200">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Installment #
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Plan Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Projected Date
                              </th>
                              <th className="px-6 py-3 w-64 text-left text-xs font-medium text-slate-500 uppercase">
                                Projected Amount
                              </th>
                              {formData.isBankLoanInvolved && (
                                <>
                                  {(() => {
                                    const isSameState = formData.customerState === formData.userState;
                                    console.log("Table Header - Customer State:", formData.customerState);
                                    console.log("Table Header - User State:", formData.userState);
                                    console.log("Table Header - States are same:", isSameState);

                                    if (isSameState) {
                                      return (
                                        <>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            CGST
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            SGST
                                          </th>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            IGST
                                          </th>
                                        </>
                                      );
                                    }
                                  })()}
                                </>
                              )}
                              {localPaymentPlan.some(installment => installment.projectedAmount) && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                  Payment Source
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-200">
                            {localPaymentPlan.map((installment, index) => {
                              return (
                                <tr key={index}>
                                  <td className="px-6 py-4">
                                    {localPaymentPlan.length === 1
                                      ? "Full Payment"
                                      : "Scheduled Payment"}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span>
                                      {localPaymentPlan.length === 1
                                        ? "Full Payment"
                                        : "Scheduled Payment"}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <CustomInput
                                      type="date"
                                      value={installment.projectedDate || ""}
                                      onChange={(e) => {
                                        const updatedPlan = [...localPaymentPlan];
                                        updatedPlan[index].projectedDate = e.target.value;
                                        setLocalPaymentPlan(updatedPlan);
                                      }}
                                      className="w-full"
                                      name={`projectedDate-${index}`}
                                    />
                                  </td>
                                  <td className="px-6 py-4">
                                    <Input.Field
                                      type="number"
                                      value={installment.projectedAmount}
                                      onChange={(e) => {
                                        const updatedPlan = [...localPaymentPlan];
                                        const enteredAmount = parseFloat(e.target.value) || 0;

                                        const totalAmount = parseFloat(formData.totalAmount) || 0;
                                        const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
                                        const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
                                        const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
                                        const totalUserResponsibility = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount;

                                        // Amounts already committed in the rows BEFORE this one.
                                        const allocatedUserAmount = updatedPlan
                                          .slice(0, index)
                                          .reduce((sum, inst) => sum + (parseFloat(inst.userAmount || 0)), 0);
                                        const allocatedBankAmount = updatedPlan
                                          .slice(0, index)
                                          .reduce((sum, inst) => sum + (parseFloat(inst.bankAmount || 0)), 0);

                                        const remainingUserAmount = Math.max(0, totalUserResponsibility - allocatedUserAmount);
                                        const remainingBankAmount = Math.max(0, bankLoanAmount - allocatedBankAmount);
                                        // The most this installment can be: whatever is still unallocated
                                        // across the buyer's responsibility AND the bank loan. Anything
                                        // beyond this previously overflowed into the bank unchecked,
                                        // producing a negative "Bank Remaining".
                                        const maxAllowed = remainingUserAmount + remainingBankAmount;

                                        let newAmount = enteredAmount;
                                        if (enteredAmount > maxAllowed) {
                                          newAmount = maxAllowed;
                                          toast.warn(
                                            `Amount capped to the remaining ${formatNumber(maxAllowed)}.`
                                          );
                                        }

                                        // Reset this row and clear subsequent rows (their allocation
                                        // depends on what's left after this one).
                                        const resetPlan = updatedPlan.map((inst, i) => {
                                          if (i === index) {
                                            return { ...inst, projectedAmount: newAmount, paymentSource: null, bankAmount: null, userAmount: null };
                                          } else if (i > index) {
                                            return { ...inst, projectedAmount: "", paymentSource: null, bankAmount: null, userAmount: null };
                                          }
                                          return inst;
                                        });

                                        // Fill the buyer's remaining responsibility first, then overflow
                                        // to the bank — but only up to the bank's remaining capacity
                                        // (guaranteed by the cap above, so bankPortion can't go negative
                                        // or exceed the loan).
                                        const userPortion = Math.min(newAmount, remainingUserAmount);
                                        const bankPortion = newAmount - userPortion;
                                        let source = "USER";
                                        if (userPortion > 0 && bankPortion > 0) source = "SPLIT";
                                        else if (bankPortion > 0) source = "BANK";

                                        resetPlan[index] = {
                                          ...resetPlan[index],
                                          projectedAmount: newAmount,
                                          paymentSource: source,
                                          userAmount: userPortion,
                                          bankAmount: bankPortion,
                                        };

                                        setLocalPaymentPlan(resetPlan);
                                      }}
                                      className="h-10 rounded-lg"
                                    />
                                  </td>
                                  {formData.isBankLoanInvolved && installment.projectedAmount && (
                                    <>
                                      {(() => {
                                        const isSameState = formData.customerState === formData.userState;
                                        console.log("Table Data - Customer State:", formData.customerState);
                                        console.log("Table Data - User State:", formData.userState);
                                        console.log("Table Data - States are same:", isSameState);

                                        if (isSameState) {
                                          return (
                                            <>
                                              <td className="px-6 py-4">
                                                <span className="font-medium text-slate-700">
                                                  {(() => {
                                                    const { amountExclGst } = calculateGSTAmounts(installment.projectedAmount);
                                                    return amountExclGst;
                                                  })()}
                                                </span>
                                              </td>
                                              <td className="px-6 py-4">
                                                <span className="font-medium text-slate-700">
                                                  {(() => {
                                                    const { cgstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                    return cgstAmount;
                                                  })()}
                                                </span>
                                              </td>
                                              <td className="px-6 py-4">
                                                <span className="font-medium text-slate-700">
                                                  {(() => {
                                                    const { sgstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                    return sgstAmount;
                                                  })()}
                                                </span>
                                              </td>
                                            </>
                                          );
                                        } else {
                                          return (
                                            <>
                                              <td className="px-6 py-4">
                                                <span className="font-medium text-slate-700">
                                                  {(() => {
                                                    const { amountExclGst } = calculateGSTAmounts(installment.projectedAmount);
                                                    return amountExclGst;
                                                  })()}
                                                </span>
                                              </td>
                                              <td className="px-6 py-4">
                                                <span className="font-medium text-slate-700">
                                                  {(() => {
                                                    const { igstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                    return igstAmount;
                                                  })()}
                                                </span>
                                              </td>
                                            </>
                                          );
                                        }
                                      })()}
                                    </>
                                  )}
                                  {installment.projectedAmount && (
                                    <td className="px-6 py-4">
                                      {(() => {
                                        const totalAmount = parseFloat(formData.totalAmount) || 0;
                                        const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
                                        const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
                                        const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;

                                        // Calculate remaining amounts up to this point
                                        const allocatedBankAmount = localPaymentPlan
                                          .slice(0, index + 1)
                                          .reduce((sum, inst) =>
                                            sum + (parseFloat(inst.bankAmount || 0)), 0);
                                        const allocatedUserAmount = localPaymentPlan
                                          .slice(0, index + 1)
                                          .reduce((sum, inst) =>
                                            sum + (parseFloat(inst.userAmount || 0)), 0);

                                        const remainingBankAmount = bankLoanAmount - allocatedBankAmount;
                                        const remainingUserAmount = (totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount) - allocatedUserAmount;

                                        if (installment.paymentSource === "BANK") {
                                          return (
                                            <div className="space-y-1">

                                              <span className="px-2 py-1 rounded-full text-sm bg-flamingo-100 font-semibold text-flamingo-700">
                                                Bank

                                              </span>
                                              <span className="ml-2 text-sm px-2 py-1 rounded-full  ">
                                                (Remaining: {formatNumber(remainingBankAmount)})
                                              </span>

                                            </div>
                                          );
                                        } else if (installment.paymentSource === "SPLIT") {
                                          return (
                                            <div className="flex flex-col gap-2">
                                              <span className="px-2 py-1 rounded-full text-sm bg-flamingo-100 font-semibold text-flamingo-700">
                                                (Bank: {formatNumber(installment.bankAmount)})
                                              </span>
                                              <span className="px-2 py-1 rounded-full text-sm bg-green-100 font-semibold text-green-800 mt-1">
                                                (User: {formatNumber(installment.userAmount)})
                                              </span>
                                              <div className="mt-1 text-xs space-y-1">
                                                <span className="text-flamingo-600">
                                                  Bank Remaining: {formatNumber(remainingBankAmount)}
                                                </span>
                                                <br />
                                                <span className="text-green-600">
                                                  User Remaining: {formatNumber(remainingUserAmount)}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <div className="space-y-1">
                                              <span className="px-2 py-1 rounded-full text-sm bg-green-100 font-semibold text-green-800">
                                                User
                                              </span>
                                              <span className="ml-2 text-sm ">
                                                (Remaining: {formatNumber(remainingUserAmount)})
                                              </span>
                                            </div>
                                          );
                                        }
                                      })()}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Total Amount Display */}
                    <div className="mt-4 p-4 border rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-medium">Total User Amount:</div>
                        <div className="font-normal">
                          {(() => {
                            const totalUserAmount = localPaymentPlan.reduce((sum, inst) =>
                              sum + (inst.paymentSource === "USER" ? parseFloat(inst.projectedAmount) || 0 : 0), 0);
                            return (
                              <span className="font-semibold text-lg text-green-600">
                                {formatNumber(totalUserAmount)}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      {formData.isBankLoanInvolved && (
                        <div className="flex justify-between items-center">
                          <div className="font-medium">Total Bank Amount:</div>
                          <div className="font-normal">
                            {(() => {
                              const totalBankAmount = localPaymentPlan.reduce((sum, inst) =>
                                sum + (inst.paymentSource === "BANK" ? parseFloat(inst.projectedAmount) || 0 : 0), 0);
                              return (
                                <span className="font-semibold text-lg text-flamingo-600">
                                  {formatNumber(totalBankAmount)}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between items-center mt-2 pt-2 border-t">
                        <div className="font-medium">Grand Total:</div>
                        <div className="font-normal">
                          {(() => {
                            const totalAmount = localPaymentPlan.reduce((sum, inst) =>
                              sum + (parseFloat(inst.projectedAmount) || 0), 0);
                            return (
                              <span className="font-semibold text-lg">
                                {formatNumber(totalAmount)}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Scheduled Payment Preview Table
                   {formData.paymentPlan && formData.paymentPlan.length > 0 && (
                     <div className="mt-6">
                       <h4 className="font-medium mb-4">Payment Schedule Preview</h4>
                       <div className="overflow-x-auto">
                         <table className="min-w-full divide-y divide-slate-200">
                           <thead className="bg-slate-50">
                             <tr>
                               <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Installment</th>
                               <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                               <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                               <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Payment Source</th>
                             </tr>
                           </thead>
                           <tbody className="bg-white divide-y divide-slate-200">
                             {formData.paymentPlan.map((payment, index) => (
                               <tr key={index} className="hover:bg-slate-50">
                                 <td className="px-4 py-3 whitespace-nowrap">
                                   {index + 1}
                                 </td>
                                 <td className="px-4 py-3 whitespace-nowrap">
                                   ₹{formatNumber(payment.projectedAmount)} 
                                 </td>
                                 <td className="px-4 py-3 whitespace-nowrap">
                                   {formatDate(payment.projectedDate)}
                                 </td>
                                 <td className="px-4 py-3 whitespace-nowrap">
                                   <span className={`px-2 py-1 rounded-full text-xs ${
                                     payment.paymentSource === "BANK" 
                                       ? "bg-flamingo-100 text-flamingo-700" 
                                       : payment.paymentSource === "SPLIT"
                                       ? "bg-purple-100 text-purple-800"
                                       : "bg-green-100 text-green-800"
                                   }`}>
                                     {payment.paymentSource || "USER"}
                                   </span>
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                           <tfoot className="bg-slate-50">
                             <tr>
                               <td className="px-4 py-3 font-medium">Total</td>
                               <td className="px-4 py-3 font-medium">
                                 ₹{formatNumber(formData.paymentPlan.reduce((sum, payment) => 
                                     sum + (parseFloat(payment.projectedAmount) || 0), 0))}
                               </td>
                               <td colSpan="2"></td>
                             </tr>
                           </tfoot>
                         </table>
                       </div>
                     </div>
                   )} */}
                </>
              ) : (
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Scheduled Payment</h4>
                    <IconButton
                      size="sm" color="black"
                      onClick={() => handleEditCard("SCHEDULED")}
                    >
                      <Edit className="h-5 w-5" />
                    </IconButton>
                  </div>

                  {/* Scheduled Payment Preview Table */}
                  {formData.paymentPlan && formData.paymentPlan.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium mb-4">Payment Schedule Preview</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Installment</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Payment Source</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Bank Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User Amount</th>
                              {formData.isBankLoanInvolved && (
                                <>
                                  {(() => {
                                    const isSameState = formData.customerState === formData.userState;
                                    if (isSameState) {
                                      return (
                                        <>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount (Excl. GST)</th>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">CGST</th>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">SGST</th>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount (Excl. GST)</th>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">IGST</th>
                                        </>
                                      );
                                    }
                                  })()}
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-200">
                            {formData.paymentPlan.map((payment, index) => (
                              <tr key={index} className="hover:bg-slate-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {index + 1}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  ₹{formatNumber(payment.projectedAmount)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {formatDate(payment.projectedDate)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`px-2 py-1 rounded-full text-xs ${payment.paymentSource === "BANK"
                                    ? "bg-flamingo-100 text-flamingo-700"
                                    : payment.paymentSource === "SPLIT"
                                      ? "bg-purple-100 text-purple-800"
                                      : "bg-green-100 text-green-800"
                                    }`}>
                                    {payment.paymentSource || "USER"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {payment.bankAmount !== undefined && payment.bankAmount !== null
                                    ? `₹${formatNumber(payment.bankAmount)}`
                                    : "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {payment.userAmount !== undefined && payment.userAmount !== null
                                    ? `₹${formatNumber(payment.userAmount)}`
                                    : "-"}
                                </td>
                                {formData.isBankLoanInvolved && (
                                  <>
                                    {(() => {
                                      const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                      const totalAmount = parseFloat(payment.projectedAmount) || 0;
                                      const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                      const { cgstAmount, sgstAmount, igstAmount } = calculateGSTAmounts(payment.projectedAmount);

                                      const isSameState = formData.customerState === formData.userState;
                                      if (isSameState) {
                                        return (
                                          <>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                              ₹{formatNumber(amountExclGst)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                              ₹{formatNumber(cgstAmount)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                              ₹{formatNumber(sgstAmount)}
                                            </td>
                                          </>
                                        );
                                      } else {
                                        return (
                                          <>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                              ₹{formatNumber(amountExclGst)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                              ₹{formatNumber(igstAmount)}
                                            </td>
                                          </>
                                        );
                                      }
                                    })()}
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50">
                            <tr>
                              <td className="px-4 py-3 font-medium">Total</td>
                              <td className="px-4 py-3 font-medium">
                                ₹{formatNumber(formData.paymentPlan.reduce((sum, payment) =>
                                  sum + (parseFloat(payment.projectedAmount) || 0), 0))}
                              </td>
                              <td colSpan={
                                3 +
                                (formData.isBankLoanInvolved
                                  ? (formData.customerState === formData.userState ? 3 : 2)
                                  : 0)
                              }></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {paymentType === "HYBRID" && (
            <div className="p-4 border rounded">
              {!savedCardStates.HYBRID ? (
                <>
                  <h3 className="font-medium mb-4">Hybrid Payment (Scheduled + Slab)</h3>
                  
                  {/* Scheduled Payments Section */}
                  <div className="mb-6">
                    {!isHybridScheduledSaved ? (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">Scheduled Payments</h4>
                          <PrimaryButton
                            onClick={handleSaveHybridScheduledPayments}
                            className="w-40"
                            disabled={isLoading || hybridScheduledPayments.length === 0}
                          >
                            {isLoading ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Saving...
                              </div>
                            ) : (
                              "Save Scheduled"
                            )}
                          </PrimaryButton>
                        </div>
                        <div className="flex items-end gap-4 mb-4">
                          <div className="flex-1">
                            <label className="block text-sm font-normal text-slate-700 mb-1">
                              Number of Installments:
                            </label>
                            <Input.Field
                              type="number"
                              name="hybridNumberOfInstallments"
                              value={hybridScheduledPayments.length || ""}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                const installments = parseInt(inputValue, 10) || 0;
                                if (
                                  installments >= 0 &&
                                  Number.isInteger(installments) &&
                                  installments < 1000
                                ) {
                                  // Create basic installment structure
                                  let newPaymentPlan = Array(installments)
                                    .fill()
                                    .map((_, index) => ({
                                      installmentNumber: index + 1,
                                      planType: "scheduled_payment",
                                      projectedAmount: "",
                                      projectedDate: null,
                                      statusTypeId: null,
                                      paymentSource: "USER",
                                      bankAmount: 0,
                                      userAmount: 0,
                                      id: crypto.randomUUID()
                                    }));

                                  // If bank loan is involved, set last few installments as bank payments
                                  if (formData.isBankLoanInvolved) {
                                    const bankLoanAmount = parseFloat(formData.loanAmount) || 0;
                                    const totalAmount = parseFloat(formData.totalAmount) || 0;
                                    const avgInstallmentAmount = totalAmount / installments;
                                    const bankInstallments = Math.ceil(bankLoanAmount / avgInstallmentAmount);

                                    newPaymentPlan = newPaymentPlan.map((installment, index) => {
                                      if (index >= installments - bankInstallments) {
                                        installment.paymentSource = "BANK";
                                      }
                                      return installment;
                                    });
                                  }

                                  setHybridScheduledPayments(newPaymentPlan);
                                } else {
                                  setHybridScheduledPayments([]);
                                  if (inputValue) {
                                    alert(
                                      "Please enter a valid positive integer for Number of Installments."
                                    );
                                  }
                                }
                              }}
                              className="h-10 w-1/4 rounded-lg"
                            />
                          </div>
                        </div>

                        {/* Scheduled Payments Table */}
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-slate-200">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Installment #
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Projected Date
                              </th>
                              <th className="px-6 py-3 w-64 text-left text-xs font-medium text-slate-500 uppercase">
                                Projected Amount
                              </th>
                              {formData.isBankLoanInvolved && (
                                <>
                                  {(() => {
                                    const isSameState = formData.customerState === formData.userState;
                                    if (isSameState) {
                                      return (
                                        <>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            CGST
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            SGST
                                          </th>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                            IGST
                                          </th>
                                        </>
                                      );
                                    }
                                  })()}
                                </>
                              )}
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Payment Source
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Bank Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                User Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-200">
                            {hybridScheduledPayments.map((installment, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4">
                                  {index + 1}
                                </td>
                                <td className="px-6 py-4">
                                  <CustomInput
                                    type="date"
                                    value={installment.projectedDate || ""}
                                    onChange={(e) => {
                                      handleHybridScheduledChange(index, 'projectedDate', e.target.value);
                                    }}
                                    className="w-full"
                                    name={`hybridProjectedDate-${index}`}
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  <Input.Field
                                    type="number"
                                    value={installment.projectedAmount}
                                  onChange={(e) => {
                                    const newAmount = e.target.value;
                                    const amountNum = parseFloat(newAmount) || 0;
                                    const currentSource = hybridScheduledPayments[index]?.paymentSource || "USER";
                                    
                                    handleHybridScheduledChange(index, 'projectedAmount', newAmount);
                                    
                                    // Update bank/user amounts based on payment source
                                    if (currentSource === "BANK") {
                                      handleHybridScheduledChange(index, 'bankAmount', amountNum);
                                      handleHybridScheduledChange(index, 'userAmount', 0);
                                    } else if (currentSource === "SPLIT") {
                                      // Keep existing split amounts or set defaults
                                      const existingBank = parseFloat(installment.bankAmount || 0);
                                      const existingUser = parseFloat(installment.userAmount || 0);
                                      handleHybridScheduledChange(index, 'bankAmount', existingBank);
                                      handleHybridScheduledChange(index, 'userAmount', existingUser);
                                    } else {
                                      handleHybridScheduledChange(index, 'userAmount', amountNum);
                                      handleHybridScheduledChange(index, 'bankAmount', 0);
                                    }
                                  }}
                                    className="h-10 rounded-lg"
                                  />
                                </td>
                                {formData.isBankLoanInvolved && installment.projectedAmount && (
                                  <>
                                    {(() => {
                                      const isSameState = formData.customerState === formData.userState;
                                      if (isSameState) {
                                        return (
                                          <>
                                            <td className="px-6 py-4">
                                              <span className="font-medium text-slate-700">
                                                {(() => {
                                                  const { amountExclGst } = calculateGSTAmounts(installment.projectedAmount);
                                                  return amountExclGst;
                                                })()}
                                              </span>
                                            </td>
                                            <td className="px-6 py-4">
                                              <span className="font-medium text-slate-700">
                                                {(() => {
                                                  const { cgstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                  return cgstAmount;
                                                })()}
                                              </span>
                                            </td>
                                            <td className="px-6 py-4">
                                              <span className="font-medium text-slate-700">
                                                {(() => {
                                                  const { sgstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                  return sgstAmount;
                                                })()}
                                              </span>
                                            </td>
                                          </>
                                        );
                                      } else {
                                        return (
                                          <>
                                            <td className="px-6 py-4">
                                              <span className="font-medium text-slate-700">
                                                {(() => {
                                                  const { amountExclGst } = calculateGSTAmounts(installment.projectedAmount);
                                                  return amountExclGst;
                                                })()}
                                              </span>
                                            </td>
                                            <td className="px-6 py-4">
                                              <span className="font-medium text-slate-700">
                                                {(() => {
                                                  const { igstAmount } = calculateGSTAmounts(installment.projectedAmount);
                                                  return igstAmount;
                                                })()}
                                              </span>
                                            </td>
                                          </>
                                        );
                                      }
                                    })()}
                                  </>
                                )}
                                <td className="px-6 py-4">
                                  {installment.projectedAmount ? (
                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`user-${index}`}
                                          checked={installment.paymentSource === "USER" || !installment.paymentSource}
                                          onChange={() => {
                                            const currentAmount = parseFloat(installment.projectedAmount) || 0;
                                            const newSource = installment.paymentSource === "USER" ? "BANK" : "USER";
                                            handleHybridScheduledChange(index, 'paymentSource', newSource);
                                            handleHybridScheduledChange(index, 'bankAmount', newSource === "BANK" ? currentAmount : 0);
                                            handleHybridScheduledChange(index, 'userAmount', newSource === "USER" ? currentAmount : 0);
                                          }}
                                          className="h-4 w-4 text-flamingo-600 focus:ring-flamingo-400 border-gray-300 rounded"
                                        />
                                        <label htmlFor={`user-${index}`} className="text-sm text-slate-700">
                                          User
                                        </label>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`bank-${index}`}
                                          checked={installment.paymentSource === "BANK"}
                                          onChange={() => {
                                            const currentAmount = parseFloat(installment.projectedAmount) || 0;
                                            const newSource = installment.paymentSource === "BANK" ? "USER" : "BANK";
                                            handleHybridScheduledChange(index, 'paymentSource', newSource);
                                            handleHybridScheduledChange(index, 'bankAmount', newSource === "BANK" ? currentAmount : 0);
                                            handleHybridScheduledChange(index, 'userAmount', newSource === "USER" ? currentAmount : 0);
                                          }}
                                          className="h-4 w-4 text-flamingo-600 focus:ring-flamingo-400 border-gray-300 rounded"
                                        />
                                        <label htmlFor={`bank-${index}`} className="text-sm text-slate-700">
                                          Bank
                                        </label>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-slate-900">
                                    {installment.bankAmount ? `₹${formatNumber(installment.bankAmount)}` : "-"}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-slate-900">
                                    {installment.userAmount ? `₹${formatNumber(installment.userAmount)}` : "-"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </>
                    ) : (
                      // Saved/Read-only table - after saving
                      <div className="p-4 bg-flamingo-50/50 border border-flamingo-100 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-flamingo-700 flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Saved Scheduled Payments
                            </h4>
                            <PrimaryButton
                              onClick={() => setIsHybridScheduledSaved(false)}
                              className="w-32"
                              size="sm"
                            >
                              Edit
                            </PrimaryButton>
                          </div>
                          <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">Installment #</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">Date</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">Amount</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">Payment Source</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">Bank Amount</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">User Amount</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                      {savedData
                        .filter(item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT)
                        .map((payment, index) => (
                          <tr key={payment.id || index} className="hover:bg-grey-50">
                            <td className="px-4 py-3 whitespace-nowrap">{index + 1}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                              {formatDate(payment.projectedDate)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                              ₹{formatNumber(payment.projectedAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                payment.paymentSource === "BANK"
                                  ? "bg-flamingo-100 text-flamingo-700"
                                  : payment.paymentSource === "SPLIT"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-green-100 text-green-800"
                              }`}>
                                {payment.paymentSource || "USER"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-slate-900">
                              {payment.bankAmount !== undefined && payment.bankAmount !== null ? `₹${formatNumber(payment.bankAmount)}` : "₹0.00"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-slate-900">
                              {payment.userAmount !== undefined && payment.userAmount !== null ? `₹${formatNumber(payment.userAmount)}` : "₹0.00"}
                            </td>
                          </tr>
                        ))}
                            </tbody>
                            <tfoot className="bg-gray-100">
                              <tr>
                                <td className="px-4 py-3 font-medium text-slate-900">Total</td>
                                <td colSpan="4"></td>
                                <td className="px-4 py-3 font-medium text-slate-900">
                                  ₹{formatNumber(
                                    savedData
                                      .filter(item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT)
                                      .reduce((sum, payment) => sum + (parseFloat(payment.projectedAmount) || 0), 0)
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                    )}
                  </div>

                  {/* Slab Payments Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-medium">Slab Payments</h4>
                        {(() => {
                          const calc = calculateRemainingForSlabs();
                          return (
                            <p className="text-sm text-slate-600 mt-1">
                              Remaining Amount for Slab Generation: <span className="font-semibold text-green-600">{formatNumber(calc.remainingAmountForSlabs)}</span>
                            </p>
                          );
                        })()}
                      </div>
                      <GradientButton
                        onClick={handleGenerateHybridSlabPayments}
                        className={"w-fit"}
                        disabled={isConfigLoading || hybridScheduledPayments.length === 0 || !isHybridScheduledSaved}
                      >
                        {isConfigLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Generating...
                          </div>
                        ) : (
                          "Generate Slab Payments"
                        )}
                      </GradientButton>
                    </div>

                    {isHybridSlabGenerated && hybridSlabPayments.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 border rounded-lg">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Slab Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Percentage</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                              {formData.isBankLoanInvolved && (
                                <>
                                  {(() => {
                                    const isSameState = formData.customerState === formData.userState;
                                    if (isSameState) {
                                      return (
                                        <>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                            CGST
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                            SGST
                                          </th>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                            Amount (Excl. GST)
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                            IGST
                                          </th>
                                        </>
                                      );
                                    }
                                  })()}
                                </>
                              )}
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Payment Source</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Bank Amount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">User Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-200">
                            {hybridSlabPayments.map((slab, index) => (
                              <tr key={index}>
                                <td className="px-4 py-2 font-medium text-slate-700">
                                  {(() => {
                                    // v2 (F8/§E): resolve SLAB by typeName via isSlab predicate (not string literal)
                                    const slabStage = slab.wbsStage || slab.statusType;
                                    const isSlabStage = isSlab(slabStage) || slab.paymentName === "SLAB";
                                    const flr = slab.floorTarget ?? slab.target;
                                    return isSlabStage ? `Slab- ${flr ?? "—"}` : (slabStage?.label || (slab.paymentName ?? slabStage?.typeName ?? "N/A"));
                                  })()}
                                </td>
                                <td className="px-4 py-2">{slab.percentConsideration}%</td>
                                <td className="px-4 py-2">{formatNumber(slab.projectedAmount)}</td>
                                {formData.isBankLoanInvolved && (
                                  <>
                                    {formData.customerState === formData.userState ? (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { cgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return cgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { sgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return sgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { igstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return igstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    )}
                                  </>
                                )}
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${slab.paymentSource === "BANK"
                                    ? "bg-flamingo-100 text-flamingo-700"
                                    : slab.paymentSource === "SPLIT"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-green-100 text-green-800"
                                    }`}>
                                    {slab.paymentSource}
                                  </span>
                                </td>
                                <td className="px-4 py-2">{slab.bankAmount ? formatNumber(slab.bankAmount) : '-'}</td>
                                <td className="px-4 py-2">{slab.userAmount ? formatNumber(slab.userAmount) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium">Hybrid Payment</h4>
                    <IconButton
                      size="sm" color="black"
                      onClick={() => handleEditCard("HYBRID")}
                    >
                      <Edit className="h-5 w-5" />
                    </IconButton>
                  </div>
                  <p className="text-slate-600 mb-4">
                    Combined payment plan with {savedData.filter(item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT).length} scheduled payments and {savedData.filter(item => item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT).length} slab payments.
                  </p>
                  
                  {/* Combined Installments Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 border rounded-lg">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                            Payment Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                            Projected Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                            Payment Source
                          </th>
                          {formData.isBankLoanInvolved && (
                            <>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                Bank Amount
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                User Amount
                              </th>
                            </>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {savedData
                          .filter(item => 
                            item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT || 
                            item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT
                          )
                          .map((installment, index) => (
                          <tr key={index} className="hover:bg-slate-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-medium text-slate-900">
                                {installment.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT 
                                  ? "Scheduled Payment" 
                                  : "Slab Payment"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-medium text-slate-900">
                                ₹{formatNumber(installment.projectedAmount)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-slate-900">
                                {installment.projectedDate ? formatDate(installment.projectedDate) : "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {installment.paymentSource && (
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  installment.paymentSource === "BANK"
                                    ? "bg-flamingo-100 text-flamingo-700"
                                    : installment.paymentSource === "SPLIT"
                                      ? "bg-purple-100 text-purple-800"
                                      : "bg-green-100 text-green-800"
                                }`}>
                                  {installment.paymentSource}
                                </span>
                              )}
                            </td>
                            {formData.isBankLoanInvolved && (
                              <>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="text-sm text-slate-900">
                                    {installment.bankAmount ? `₹${formatNumber(installment.bankAmount)}` : "-"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="text-sm text-slate-900">
                                    {installment.userAmount ? `₹${formatNumber(installment.userAmount)}` : "-"}
                                  </span>
                                </td>
                              </>
                            )}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-slate-900">
                                {installment.paymentName || installment.details || "-"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                        <tr>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            Total
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            ₹{formatNumber(
                              savedData
                                .filter(item => 
                                  item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT || 
                                  item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT
                                )
                                .reduce((sum, installment) => 
                                  sum + (parseFloat(installment.projectedAmount) || 0), 0
                                )
                            )}
                          </td>
                          <td colSpan={formData.isBankLoanInvolved ? 4 : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {paymentType === "SLAB_WISE" && (
            <div className="p-4 border rounded">
              {!savedCardStates.SLAB_WISE ? (
                <>
                  <h3 className="font-medium mb-4">Slab Wise Payment</h3>
                  <div className="flex gap-4 mb-4">
                    <GradientButton
                      onClick={handleAutoGenerateSlabData}
                      className={"w-fit"}
                      disabled={isConfigLoading}
                    >
                      {isConfigLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Generating...
                        </div>
                      ) : (
                        "Auto-Generate Slab Data"
                      )}
                    </GradientButton>
                    {/* <PrimaryButton
                      onClick={() => handleSaveCard("SLAB_WISE")}
                      className="w-28"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </div>
                      ) : (
                        "Save"
                      )}
                    </PrimaryButton> */}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 border rounded-lg">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Slab Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Percentage</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>

                          {formData.isBankLoanInvolved && (
                            <>
                              {(() => {
                                const isSameState = formData.customerState === formData.userState;


                                if (isSameState) {
                                  return (
                                    <>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                        Amount (Excl. GST)
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        CGST
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        SGST
                                      </th>
                                    </>
                                  );
                                } else {
                                  return (
                                    <>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                        Amount (Excl. GST)
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        IGST
                                      </th>
                                    </>
                                  );
                                }
                              })()}
                            </>
                          )}

                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Payment Source</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Bank Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">User Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {(() => {
                          const slabRows = savedData.filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT);
                          const slabIndices = savedData.reduce((acc, item, i) => {
                            if (item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT) acc.push(i);
                            return acc;
                          }, []);
                          const totalSlabPercent = slabRows.reduce((sum, s) => sum + (parseFloat(s.percentConsideration) || 0), 0);
                          const totalSlabProjectedAmount = slabRows.reduce((sum, s) => sum + (parseFloat(s.projectedAmount) || 0), 0);
                          const totalAmount = parseFloat(formData.totalAmount) || 0;
                          const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
                          const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
                          const bankLoanAmount = formData.isBankLoanInvolved ? (parseFloat(formData.loanAmount) || 0) : 0;
                          const totalScheduledAmount = paymentType === "HYBRID"
                            ? hybridScheduledPayments.reduce((sum, p) => sum + (parseFloat(p.projectedAmount) || 0), 0)
                            : 0;
                          const remainingForSlabs = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount - totalScheduledAmount;
                          const isAmountNegative = remainingForSlabs < 0 || totalSlabProjectedAmount > remainingForSlabs;
                          const isPercentOver100 = totalSlabPercent > 100 || isAmountNegative;
                          return slabRows.map((slab, index) => {
                            const realIndex = slabIndices[index];
                            // v2 (F8/§E): resolve SLAB by typeName via isSlab predicate (not string literal)
                            const slabStage = slab.wbsStage || slab.statusType;
                            const isSlabType = isSlab(slabStage) || slab.paymentName === "SLAB";
                            const flrTarget = slab.floorTarget ?? slab.target;
                            const slabName = isSlabType ? `Slab- ${flrTarget ?? "—"}` : (slabStage?.label || (slab.paymentName ?? slabStage?.typeName ?? "N/A"));
                            return (
                              <tr key={slab.id || index}>
                                <td className="px-4 py-2 font-medium text-slate-700">
                                  <div>{slabName}</div>
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    className={`w-16 px-2 py-1 border rounded text-sm ${isPercentOver100 ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-slate-300"}`}
                                    value={slab.percentConsideration ?? ""}
                                    onChange={(e) => handleSlabChange(realIndex, "percentConsideration", e.target.value)}
                                  />
                                  <span className="ml-1 text-slate-600">%</span>
                                </td>
                                <td className="px-4 py-2">{formatNumber(slab.projectedAmount)}</td>
                                {formData.isBankLoanInvolved && (
                                  <>
                                    {formData.customerState === formData.userState ? (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { cgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return cgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { sgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return sgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { igstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return igstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    )}
                                  </>
                                )}
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${slab.paymentSource === "BANK"
                                    ? "bg-flamingo-100 text-flamingo-700"
                                    : slab.paymentSource === "SPLIT"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-green-100 text-green-800"
                                    }`}>
                                    {slab.paymentSource}
                                  </span>
                                </td>
                                <td className="px-4 py-2">{slab.bankAmount ? formatNumber(slab.bankAmount) : '-'}</td>
                                <td className="px-4 py-2">{slab.userAmount ? formatNumber(slab.userAmount) : '-'}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium">Slab Wise Payment</h4>
                    <div className="flex gap-2">
                      {/* <PrimaryButton
                        onClick={() => handleSaveCard("SLAB_WISE")}
                        className="w-28"
                      >
                        Save
                      </PrimaryButton> */}
                      <IconButton
                        size="sm" color="black"
                        onClick={() => handleEditCard("SLAB_WISE")}
                      >
                        <Edit className="h-5 w-5" />
                      </IconButton>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 border rounded-lg" style={{ minWidth: '1200px' }}>
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-48">Slab Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-20">Percentage</th>


                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">Payment Source</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">Bank Amount</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">User Amount</th>
                          {formData.isBankLoanInvolved && (
                            <>
                              {(() => {
                                const isSameState = formData.customerState === formData.userState;
                                console.log("Slab Table 2 Header - Customer State:", formData.customerState);
                                console.log("Slab Table 2 Header - User State:", formData.userState);
                                console.log("Slab Table 2 Header - States are same:", isSameState);

                                if (isSameState) {
                                  return (
                                    <>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                        Amount (Excl. GST)
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        CGST
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        SGST
                                      </th>
                                    </>
                                  );
                                } else {
                                  return (
                                    <>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-28">
                                        Amount (Excl. GST)
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-24">
                                        IGST
                                      </th>
                                    </>
                                  );
                                }
                              })()}
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {(() => {
                          const slabRows = savedData.filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT);
                          const slabIndices = savedData.reduce((acc, item, i) => {
                            if (item.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT) acc.push(i);
                            return acc;
                          }, []);
                          const totalSlabPercent = slabRows.reduce((sum, s) => sum + (parseFloat(s.percentConsideration) || 0), 0);
                          const totalSlabProjectedAmount = slabRows.reduce((sum, s) => sum + (parseFloat(s.projectedAmount) || 0), 0);
                          const totalAmount = parseFloat(formData.totalAmount) || 0;
                          const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
                          const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;
                          const bankLoanAmount = formData.isBankLoanInvolved ? (parseFloat(formData.loanAmount) || 0) : 0;
                          const totalScheduledAmount = paymentType === "HYBRID"
                            ? hybridScheduledPayments.reduce((sum, p) => sum + (parseFloat(p.projectedAmount) || 0), 0)
                            : 0;
                          const remainingForSlabs = totalAmount - bankLoanAmount - tokenAmount - downPaymentAmount - totalScheduledAmount;
                          const isAmountNegative = remainingForSlabs < 0 || totalSlabProjectedAmount > remainingForSlabs;
                          const isPercentOver100 = totalSlabPercent > 100 || isAmountNegative;
                          return slabRows.map((slab, index) => {
                            const realIndex = slabIndices[index];
                            // v2 (F8/§E): resolve SLAB by typeName via isSlab predicate (not string literal)
                            const slabStageV2 = slab.wbsStage || slab.statusType;
                            const isSlabType = isSlab(slabStageV2) || slab.paymentName === "SLAB";
                            const flrTargetV2 = slab.floorTarget ?? slab.target;
                            const slabName = isSlabType ? `Slab- ${flrTargetV2 ?? "—"}` : (slabStageV2?.label || (slab.paymentName ?? slabStageV2?.typeName ?? "N/A"));
                            return (
                              <tr key={slab.id != null ? slab.id : index}>
                                <td className="px-4 py-2 font-medium text-slate-700">
                                  <div>{slabName}</div>
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    className={`w-16 px-2 py-1 border rounded text-sm ${isPercentOver100 ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-slate-300"}`}
                                    value={slab.percentConsideration ?? ""}
                                    onChange={(e) => handleSlabChange(realIndex, "percentConsideration", e.target.value)}
                                  />
                                  <span className="ml-1 text-slate-600">%</span>
                                </td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${slab.paymentSource === "BANK"
                                    ? "bg-flamingo-100 text-flamingo-700"
                                    : slab.paymentSource === "SPLIT"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-green-100 text-green-800"
                                    }`}>
                                    {slab.paymentSource}
                                  </span>
                                </td>
                                <td className="px-4 py-2">{slab.bankAmount ? formatNumber(slab.bankAmount) : '-'}</td>
                                <td className="px-4 py-2">{slab.userAmount ? formatNumber(slab.userAmount) : '-'}</td>
                                {formData.isBankLoanInvolved && (
                                  <>
                                    {formData.customerState === formData.userState ? (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { cgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return cgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { sgstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return sgstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const gstPercent = property?.projectPhase?.project?.configuration?.gstPercent?.[0] || 0;
                                              const totalAmount = parseFloat(slab.projectedAmount) || 0;
                                              const amountExclGst = totalAmount / (1 + (gstPercent / 100));
                                              return formatNumber(amountExclGst);
                                            })()}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className="font-medium text-slate-700">
                                            {(() => {
                                              const { igstAmount } = calculateGSTAmounts(slab.projectedAmount);
                                              return igstAmount;
                                            })()}
                                          </span>
                                        </td>
                                      </>
                                    )}
                                  </>
                                )}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-6 py-4 p-4 flex flex-col gap-2 rounded-md border">
          <div className="flex justify-between items-center">
            <div className="font-medium">Total Value:</div>
            <div className="font-normal">
              <span className="font-semibold text-lg">{formatNumber(formData.totalAmount)}</span>
            </div>
          </div>
          <div className="flex justify-between items-center border-t pt-2">
            <div className="font-medium">Remaining Amount payable by user:</div>
            <div className="font-normal">
              {(() => {
                const totalAmount = parseFloat(formData.totalAmount) || 0;
                const bankLoanAmount = formData.isBankLoanInvolved ? parseFloat(formData.loanAmount) || 0 : 0;
                const tokenAmount = parseFloat(formData.tokenTotalAmount) || 0;
                const downPaymentAmount = parseFloat(formData.downPaymentTotalAmount) || 0;

                let totalUserAmount = 0;
                if (paymentType === "SCHEDULED") {
                  totalUserAmount = localPaymentPlan.reduce((sum, inst) =>
                    sum + (parseFloat(inst.userAmount || 0)), 0);
                } else if (paymentType === "SLAB_WISE") {
                  totalUserAmount = savedData
                    .filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT)
                    .reduce((sum, slab) =>
                      sum + (parseFloat(slab.userAmount || 0)), 0);
                } else if (paymentType === "HYBRID") {
                  // Calculate user amount from both scheduled and slab payments in savedData
                  const scheduledUserAmount = savedData
                    .filter(item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT)
                    .reduce((sum, payment) => sum + (parseFloat(payment.userAmount || payment.projectedAmount || 0)), 0);
                  const slabUserAmount = savedData
                    .filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT)
                    .reduce((sum, slab) => sum + (parseFloat(slab.userAmount || slab.projectedAmount || 0)), 0);
                  totalUserAmount = scheduledUserAmount + slabUserAmount;
                }

                const remainingUserAmount = Math.round(((totalAmount - bankLoanAmount) - (totalUserAmount + tokenAmount + downPaymentAmount)) * 100) / 100;
                
                // Debug logging for hybrid payments
                if (paymentType === "HYBRID") {
                  console.log("Hybrid Payment Summary Calculation:");
                  console.log("Total Amount:", totalAmount);
                  console.log("Bank Loan Amount:", bankLoanAmount);
                  console.log("Token Amount:", tokenAmount);
                  console.log("Down Payment Amount:", downPaymentAmount);
                  console.log("Total User Amount:", totalUserAmount);
                  console.log("Remaining User Amount:", remainingUserAmount);
                }

                return (
                  <span className={`font-semibold text-lg ${remainingUserAmount < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                    {formatNumber(remainingUserAmount)}
                  </span>
                );
              })()}
            </div>
          </div>

          {formData.isBankLoanInvolved && (
            <>
              <div className="flex justify-between items-center border-t pt-2">
                <div className="font-medium">Total Bank Loan Amount:</div>
                <div className="font-normal">
                  <span className="font-semibold text-lg text-flamingo-600">
                    {formatNumber(formData.loanAmount)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <div className="font-medium">Remaining Bank Loan Amount:</div>
                <div className="font-normal">
                  {(() => {
                    const totalBankAmount = parseFloat(formData.loanAmount) || 0;
                    let allocatedBankAmount = 0;

                    if (paymentType === "SCHEDULED") {
                      allocatedBankAmount = localPaymentPlan.reduce((sum, inst) =>
                        sum + (parseFloat(inst.bankAmount || 0)), 0);
                    } else if (paymentType === "SLAB_WISE") {
                      allocatedBankAmount = savedData
                        .filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT)
                        .reduce((sum, slab) =>
                          sum + (parseFloat(slab.bankAmount || 0)), 0);
                    } else if (paymentType === "HYBRID") {
                      // Calculate bank amount from both scheduled and slab payments in savedData
                      const scheduledBankAmount = savedData
                        .filter(item => item.paymentTypeId === paymentTypeIds.SCHEDULED_PAYMENT)
                        .reduce((sum, payment) => sum + (parseFloat(payment.bankAmount || 0)), 0);
                      const slabBankAmount = savedData
                        .filter(slab => slab.paymentTypeId === paymentTypeIds.SLAB_WISE_PAYMENT)
                        .reduce((sum, slab) => sum + (parseFloat(slab.bankAmount || 0)), 0);
                      allocatedBankAmount = scheduledBankAmount + slabBankAmount;
                    }

                    const remainingBankAmount = Math.round((totalBankAmount - allocatedBankAmount) * 100) / 100;

                    return (
                      <span className={`font-semibold text-lg ${remainingBankAmount < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                        {formatNumber(remainingBankAmount)}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>

       
      </div>
    </>
  );
};

export default PaymentTable;